import { useRef, useState, useEffect } from "react";
import { FaDiscord, FaXTwitter } from "react-icons/fa6";
import { FaTelegramPlane } from "react-icons/fa";
import { Users, Bell } from "lucide-react";

// Custom hook for intersection observer (iOS-safe)
const useIsVisible = (ref, options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (!ref.current) return;
    
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        if (options.once !== false) {
          observer.disconnect();
        }
      }
    }, { threshold: options.amount || 0.3 });
    
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, options.once, options.amount]);
  
  return isVisible;
};

const SocialCard = ({ social, index }) => {
  const ref = useRef(null);
  const isVisible = useIsVisible(ref, { once: true, amount: 0.3 });

  return (
    <a
      ref={ref}
      href={social.link}
      target="_blank"
      rel="noopener noreferrer"
      className={`glass rounded-2xl p-6 text-center group transition-all duration-500 transform-gpu hover:-translate-y-1 hover:scale-[1.02] ${social.color} ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ 
        transitionDelay: `${index * 0.1}s`,
        WebkitBackfaceVisibility: 'hidden',
      }}
    >
      <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-white/20 transition-colors">
        <social.icon className="w-7 h-7 text-white" />
      </div>
      <h4 className="text-lg font-semibold text-white mb-1">{social.name}</h4>
      <p className="text-gray-400 text-sm mb-3">{social.desc}</p>
      <span className="text-purple-400 text-sm font-medium">{social.count} {social.label}</span>
    </a>
  );
};

const socials = [
  {
    name: "Twitter / X",
    desc: "Latest updates & announcements",
    icon: FaXTwitter,
    link: "https://x.com/RefundYourSOL",
    color: "hover:bg-gray-800",
    count: "23K+",
    label: "followers",
  },
  {
    name: "Telegram News",
    desc: "Official news & updates",
    icon: Bell,
    link: "https://t.me/refundyoursol",
    color: "hover:bg-blue-600/20",
    count: "19K+",
    label: "subscribers",
  },
  {
    name: "Telegram Community",
    desc: "Chat with the community",
    icon: FaTelegramPlane,
    link: "https://t.me/refundyoursolcom",
    color: "hover:bg-blue-600/20",
    count: "6K+",
    label: "members",
  },
  {
    name: "Discord",
    desc: "Join discussions & get support",
    icon: FaDiscord,
    link: "https://discord.gg/8RPqhgyjXt",
    color: "hover:bg-indigo-600/20",
    count: "6K+",
    label: "members",
  },
];

const stats = [
  { icon: Users, label: "Community Members", value: "54,000+" },
];

export default function Community() {
  const headingRef = useRef(null);
  const headingVisible = useIsVisible(headingRef, { once: true });

  return (
    <section id="community" className="relative py-24 bg-[#0b0b0f] border-t border-white/5 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(168,85,247,0.1),transparent_60%)]" />
      
      <div className="relative max-w-5xl mx-auto px-6">
        <div
          ref={headingRef}
          className={`text-center mb-12 transition-all duration-600 ${
            headingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
          }`}
          style={{ WebkitBackfaceVisibility: 'hidden' }}
        >
          <h3 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">
            Join the Community
          </h3>
          <p className="text-gray-400 max-w-xl mx-auto">
            Be part of the growing RefundYourSOL ecosystem. Connect with traders, developers, and enthusiasts.
          </p>
        </div>

        {/* Community Stats */}
        <div className="grid grid-cols-1 gap-4 mb-12 max-w-md mx-auto">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className={`text-center transition-all duration-500 ${
                headingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
              }`}
              style={{ 
                transitionDelay: `${i * 0.1}s`,
                WebkitBackfaceVisibility: 'hidden',
              }}
            >
              <stat.icon className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-xs text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Social Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 max-w-4xl mx-auto">
          {socials.map((social, i) => (
            <SocialCard key={social.name} social={social} index={i} />
          ))}
        </div>

        {/* CTA */}
        <p
          className={`text-center text-gray-500 text-sm mt-10 transition-opacity duration-600 ${
            headingVisible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDelay: '0.4s', WebkitBackfaceVisibility: 'hidden' }}
        >
          Weekly community calls, AMAs, and exclusive alpha for holders
        </p>
      </div>
    </section>
  );
}
